'use client';
import { appConfig } from '../../utils/appConfig';
import NavDropdownWrapper from './_NavDropdownWrapper';
import { useNavigationContext } from './index';
import { Anchor } from '@repo/ui/src/components/Anchor';
import ChevronIcon from '@repo/ui/src/components/Icons/ChevronIcon';
import { MetaProps } from '@repo/ui/src/types';
import React, { useState } from 'react';
import NavDropdown, { NavDropdownProps } from './_NavDropdown';
import { classNames } from '@repo/ui/src/utils/classNames';
import { LinkProps } from '@repo/ui/src/types/link';
import { navigationClicked } from '@repo/ui/src/services/analytics';

export interface SecondaryNavProps {
  _meta: MetaProps;
  items: Array<NavDropdownProps | LinkProps>;
  open: boolean;
  primaryHeader?: string;
  useKeyboardNavigation?: (options: any) => (e: React.KeyboardEvent) => void;
}

export const isNavDropdown = (
  navItem: NavDropdownProps | LinkProps,
): navItem is NavDropdownProps => {
  return !!(navItem._meta?.schema && navItem._meta.schema.endsWith('navDropdown.json'));
};

const SecondaryNav = ({
  items = [],
  open,
  primaryHeader,
  useKeyboardNavigation,
}: SecondaryNavProps) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [openDropdown, setOpenDropdown] = useState<number | null>(0);
  const { setHeight } = useNavigationContext();
  const resetDropdown = () => setOpenDropdown(0);

  const handleKeyDown = useKeyboardNavigation?.({
    items,
    onChange: (index: number) => {
      if (isNavDropdown(items[index])) {
        setOpenDropdown(index);
      } else {
        setOpenDropdown(null);
        setHeight(0);
      }
      (
        ref.current?.querySelector(`[data-testid="secondary-nav-${index}"]`) as HTMLElement
      )?.focus();
    },
  });

  if (!items.length) return null;

  return (
    <div role="region" aria-label={`${primaryHeader} submenu`} ref={ref}>
      <ul
        className={classNames(
          'list-none grid gap-x-8 sm:flex sm:justify-center sm:gap-x-8 sm:flex-wrap',
        )}
      >
        {items?.map((item, i) => {
          const itemOpen = openDropdown === i;
          const hasDropdown = isNavDropdown(item);
          const id = `secondary-nav-${i}`;
          return (
            <li key={i} className="px-4 sm:px-0">
              {!hasDropdown ? (
                <Anchor
                  {...item}
                  onKeyDown={handleKeyDown}
                  dataTestId={id}
                  data-index={i}
                  onClick={() => {
                    setOpenDropdown(null);
                    setHeight(0);
                    navigationClicked({
                      nav_type: 'Top Navigation',
                      clicked_text: item.displayName ?? '',
                      primary_header: primaryHeader ?? '',
                    });
                  }}
                  className={classNames(
                    'inline-block tra-text-web-body-l hover:text-brand-400 hover:underline transition-all duration-300 py-2',
                    itemOpen && 'text-brand-400 underline',
                  )}
                />
              ) : (
                <button
                  onClick={() => {
                    setOpenDropdown(i);
                    navigationClicked({
                      nav_type: 'Top Navigation',
                      clicked_text: item.title ?? '',
                      primary_header: primaryHeader ?? '',
                      secondary_header: item.title ?? '',
                    });
                  }}
                  onKeyDown={handleKeyDown}
                  className={classNames(
                    'tra-text-web-body-l flex justify-between w-full sm:w-auto sm:block hover:text-brand-400 hover:underline transition-all duration-300 py-2',
                    itemOpen && '!text-brand-400 underline',
                    appConfig.isMeater && 'text-neutral-700',
                  )}
                  aria-expanded={itemOpen ? 'true' : 'false'}
                  aria-haspopup={hasDropdown ? 'true' : 'false'}
                  aria-controls={id}
                  data-index={i}
                  data-testid={id}
                >
                  {item.title}
                  <span className="w-6 inline-block sm:hidden" aria-hidden="true">
                    <ChevronIcon direction="right" />
                  </span>
                </button>
              )}
              {hasDropdown && (
                <NavDropdownWrapper
                  id={id}
                  open={open && openDropdown === i && !!items?.length}
                  isDropdown={isNavDropdown(item)}
                  onTransitionEnd={() => {
                    if (!open) {
                      resetDropdown();
                    }
                  }}
                >
                  <NavDropdown
                    {...item}
                    primaryHeader={primaryHeader}
                    secondaryHeader={item?.title}
                  />
                </NavDropdownWrapper>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default SecondaryNav;
